import type { JarvisBenchState, JarvisBenchSummary } from "../types/jarvis";

interface BenchResponse {
  ok: boolean;
  state: JarvisBenchState;
  report: { summary: JarvisBenchSummary | null; version?: number; generatedAt?: string } | null;
  logTail?: string | null;
  error?: string;
}

interface BenchStartResponse {
  ok: boolean;
  state: JarvisBenchState;
  error?: string;
}

export interface JarvisBenchStartInput {
  environment: string;
  provider?: string;
  providerEnv?: Record<string, string>;
  workspaceId?: string;
}

async function readResponsePayload<T>(
  response: Response,
): Promise<{ payload: T | null; rawText: string }> {
  const rawText = await response.text();
  if (!rawText) {
    return { payload: null, rawText: "" };
  }

  try {
    return { payload: JSON.parse(rawText) as T, rawText };
  } catch {
    return { payload: null, rawText };
  }
}

function handleError(response: Response, payload: { error?: string } | null, rawText: string, fallback: string): never {
  const fallbackText = rawText && rawText.length <= 160 ? `: ${rawText}` : "";
  const message = payload?.error ?? `${fallback} (status ${response.status})${fallbackText}`;
  throw new Error(message);
}

export async function fetchJarvisBench(): Promise<{ state: JarvisBenchState; report: BenchResponse["report"]; logTail?: string | null }> {
  const response = await fetch("/api/jarvis/bench");
  const { payload, rawText } = await readResponsePayload<BenchResponse>(response);

  if (!response.ok || payload?.ok === false || !payload?.state) {
    handleError(response, payload, rawText, "Failed to load Jarvis bench status");
  }

  return { state: payload.state, report: payload.report, logTail: payload.logTail };
}

export async function startJarvisBench(input: JarvisBenchStartInput): Promise<JarvisBenchState> {
  const response = await fetch("/api/jarvis/bench", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const { payload, rawText } = await readResponsePayload<BenchStartResponse>(response);

  if (!response.ok || payload?.ok === false || !payload?.state) {
    handleError(response, payload, rawText, "Failed to start Jarvis bench");
  }

  return payload.state;
}
